import { ONBOARDING_STEPS } from "@repo/contract/training";
/**
 * The `openScreen` handoff, on the device's side (`D112` · `D123`).
 *
 * ── THE APP DECIDES WHAT THE CONTROL SAYS, ALWAYS ───────────────────────────
 * The model names a screen and never supplies display text, for the reason
 * `D27` keeps sentences off the wire: a label is copy, copy is localized, and
 * a sentence written by a model in the wrong language is a sentence no
 * translation pass can reach. So what arrives is one enum member, and
 * {@link SCREEN_LABEL_KEY} turns it into a key the locale files own.
 *
 * ── THE ENUM IS RESTATED, AND REFUSING IS THE POINT ─────────────────────────
 * `apps/ai/src/tools/open-screen-tool.ts` holds the schema the model is bound
 * by; this app cannot import it (rule 12 — a workerd package would pull `ai`,
 * the Anthropic SDK and the Durable Object machinery into the mobile bundle).
 * The restatement is not a convenience: a client that trusted whatever string
 * arrived would turn a model into a router, and a route from a model is an open
 * redirect into the app's own navigation with the person's session behind it.
 * {@link screenOffer} is therefore a REFUSAL first — anything not in the set
 * below produces no control at all, which is the same silence a malformed
 * `askUser` call gets (`parseAsk`'s rule: one unrenderable control must not
 * cost the answer it arrived with).
 *
 * ── WHY UNKNOWN KEYS ARE IGNORED AND MISSING ONES ARE NOT ───────────────────
 * The Worker deploys in seconds; the app arrives over OTA whenever the phone
 * next opens. So a server that has grown an optional field must still be
 * readable by a build that predates it — the extra key is dropped, exactly as
 * the contract package's own rule requires. A field this build NEEDS is the
 * other direction and cannot be forgiven: `plan_day_editor` without a
 * `draftId` and a `sessionId` addresses nothing, and rendering a control that
 * opens an editor over no draft would spend the person's tap on a dead end.
 */
/**
 * Every screen the trainer may offer. The server's copy is
 * `OPEN_SCREEN_SCREENS`; the two are the same list or the handoff is broken in
 * one direction, silently.
 */
export const OFFERABLE_SCREENS = [
    "plan_week",
    "plan_day_editor",
    "intake",
    "settings",
];
/**
 * The label each screen is offered under.
 *
 * A `Record`, not a template string: `chat.open_screen.${screen}` would type
 * -check against any string at all and render `error.unrecognized` at the
 * moment a screen is added — the one moment the person is being asked to trust
 * the control. Typed as {@link MessageKey}, the missing entry is a build error
 * in this file instead.
 */
export const SCREEN_LABEL_KEY = {
    intake: "chat.open_screen.intake",
    plan_day_editor: "chat.open_screen.plan_day_editor",
    plan_week: "chat.open_screen.plan_week",
    settings: "chat.open_screen.settings",
};
function isOfferableScreen(value) {
    return (typeof value === "string" &&
        OFFERABLE_SCREENS.includes(value));
}
function isOnboardingStep(value) {
    return (typeof value === "string" &&
        ONBOARDING_STEPS.includes(value));
}
function nonEmptyString(value) {
    return typeof value === "string" && value !== "" ? value : null;
}
/**
 * One `tool-input-available` input, as an offer this app can render — or
 * `null`, which means no control is drawn and the answer stands on its prose.
 */
export function screenOffer(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
        return null;
    }
    const { draftId, screen, sessionId, step } = input;
    if (!isOfferableScreen(screen)) {
        return null;
    }
    if (screen === "intake") {
        // Plan D31: the server's `step` is the progress vocabulary, the same
        // `ONBOARDING_STEPS` imported here. A step this build does not know — a
        // newer server — is DROPPED rather than refused: the offer still opens
        // the intake, which is a correct place to land, just not the exact step.
        return isOnboardingStep(step) ? { screen, step } : { screen };
    }
    if (screen !== "plan_day_editor") {
        // The other three are addresses with no argument. A `draftId` alongside one
        // of them is dropped rather than refused: it addresses nothing here, and
        // the offer itself is still perfectly openable.
        return { screen };
    }
    const draft = nonEmptyString(draftId);
    const session = nonEmptyString(sessionId);
    if (draft === null || session === null) {
        // `T-145` / `D150` in its other form: the editor LOADS a draft and never
        // generates one, so an offer with no draft to load is a control that can
        // only fail after it has been tapped.
        return null;
    }
    return { draftId: draft, screen, sessionId: session };
}
/** The tool result for an offer the person accepted. */
export function screenOfferResult(offer) {
    return { opened: true, screen: offer.screen };
}
//# sourceMappingURL=open-screen.js.map